'use client';
/* eslint-disable react/no-danger */

import { useEffect, useState } from 'react';
import { BaseTranslations } from '../../baseTranslations';
import { AttrPaymentNetwork, AttrPaymentType } from '../../types/attributes';
import { useCmsClientContext } from '../../utils/cmsPageContext';
import {
  getPaymentBadgeSources,
  PaymentBadgeSource,
  resolvePaymentBadgeIcon,
} from '../../utils/paymentBadgeIcon';
import { getClientTranslations } from '../../utils/translations/getClientTranslations';
import Button from '../Button';
import { CmsImage } from '../CmsImage/CmsImage';
import { Icon } from '../Icon';
import ArrowUp from '../icons/ArrowUp';
import CheckCircle from '../icons/CheckCircle';
import Like from '../icons/Like';
import { WrapperInfoBlock } from './ProductContent';

const iconMap = (source: PaymentBadgeSource) => {
  const badgeIcon = resolvePaymentBadgeIcon(source);
  if (!badgeIcon) return undefined;
  return (
    <span className="inline-flex h-3.5 w-auto shrink-0 [&>*]:h-full [&>*]:w-auto">
      {badgeIcon.type === 'logo' ? (
        <CmsImage
          src={badgeIcon.logo.url}
          alt={badgeIcon.logo.alternativeText ?? ''}
          width={badgeIcon.logo.width ?? 48}
          height={badgeIcon.logo.height ?? 32}
          sizes={{ _: 48 }}
        />
      ) : (
        <Icon icon={badgeIcon.icon} />
      )}
    </span>
  );
};

const AttributePayment: React.FC<AttrPaymentNetwork | AttrPaymentType> = (
  attribute,
) => {
  const { label } = attribute;
  const sources = getPaymentBadgeSources(attribute);
  const context = useCmsClientContext();
  const staticText = getClientTranslations(BaseTranslations, context);

  const [toggleList, setToggleList] = useState<{ [key: string]: boolean }>({});
  const [isScreenSizeDesktop, setIsScreenSizeDesktop] = useState(false);

  const handleToggle = (title: string) => {
    setToggleList((prev) => ({ ...prev, [title]: !prev[title] }));
  };

  useEffect(() => {
    const checkScreenSize = () =>
      setIsScreenSizeDesktop(window.innerWidth >= 1280);

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const isExpanded = toggleList[label ?? ''] || isScreenSizeDesktop;
  const visibleItems = isExpanded ? sources : sources.slice(0, 3);

  const dataItems = visibleItems.map((source) => {
    const icon = iconMap(source);

    return (
      <div key={source.label} className="flex items-center gap-2">
        {icon ? icon : <CheckCircle className="text-campaign/green-3" />}{' '}
        {source.label}
      </div>
    );
  });

  return (
    <>
      <WrapperInfoBlock icon={<Like />} title={label ?? ''}>
        {dataItems.length ? (
          <ul>
            {dataItems.map((item, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <li key={index} className="mb-2 last:mb-0 md:mb-4">
                <span className="quick-facts ml-1 flex items-center font-medium text-sm leading-line-height/lg-relaxed md:text-lg  md:leading-line-height/xl">
                  {item}
                </span>
              </li>
            ))}
          </ul>
        ) : null}
      </WrapperInfoBlock>

      {sources.length > 3 && !isScreenSizeDesktop && (
        <div className="mt-2 md:hidden">
          <Button
            onClick={() => handleToggle(label ?? '')}
            variant="text"
            className="pl-0 text-text/link"
            endIcon={
              toggleList[label ?? ''] ? (
                <ArrowUp className="text-link" />
              ) : (
                <ArrowUp className="rotate-180 transition-transform text-link" />
              )
            }
          >
            {toggleList[label ?? '']
              ? staticText?.show_less
              : `${staticText?.show_more} (${sources.length - 3})`}
          </Button>
        </div>
      )}
    </>
  );
};

export default AttributePayment;
